import type { ContractCallRequest } from './types';

/**
 * Which prompt of a sequence a contract call is on (contracts v3, UI §2; api#380 §2.9).
 *
 * A call is one or two wallet prompts — an approve, if the call needs an allowance, and the call
 * itself. On its own it counts from one: "Step 1 of 2 — approve the amount", "Step 2 of 2 —
 * confirm the payment". When a screen chains several calls (creating the deal contract, then the
 * approve and the payment into it), the later call carries `progress: { offset, of }` and its
 * prompts continue the count: "Step 2 of 3", "Step 3 of 3". Pure, so the two wallet families
 * number their prompts identically and a test needs no wallet to prove it.
 */

export const APPROVE_STEP_LABEL = 'approve the amount';
export const CALL_STEP_LABEL = 'confirm the payment';

export interface StepPlan {
  /** The approve prompt's number; absent when the call needs no allowance. */
  approveStep?: number;
  /** The call prompt's number. */
  callStep: number;
  /** The whole sequence, as the buyer is told it. */
  of: number;
  callLabel: string;
}

export const stepPlan = (
  request: Pick<ContractCallRequest, 'approve' | 'progress' | 'stepLabel'>
): StepPlan => {
  const offset = request.progress?.offset ?? 0;
  const own = request.approve ? 2 : 1;

  return {
    approveStep: request.approve ? offset + 1 : undefined,
    callStep: offset + own,
    of: request.progress?.of ?? offset + own,
    callLabel: request.stepLabel ?? CALL_STEP_LABEL,
  };
};
